/**
 * Monta URLs de imagem via ImageBoss — mesmo CDN usado no cadastro de produtos
 * do sischef-app (ver `MultiImageInput.js`). O `publicId` é a key do objeto no
 * bucket de imagens (Linode `sischef-images`, *source* de mesmo nome no ImageBoss).
 *
 * Operações do ImageBoss (https://imageboss.me/docs): `cover`, `width`, `height`.
 * O ImageBoss NÃO tem uma operação de "caixa" (fit/contain/pad), então:
 *
 *  - `cover` → redimensiona e RECORTA pra preencher exatamente LxA. Bom pra
 *    banners onde dá pra perder as bordas, ruim pra fotos de produto com
 *    proporções variadas (uma lata, mais alta que larga, fica cortada).
 *  - `width` → fixa a largura e calcula a altura proporcional. Nunca recorta,
 *    preserva a imagem inteira. Combinado com `object-fit: contain` na <img>,
 *    a imagem aparece centralizada e completa em qualquer proporção.
 *
 * Por isso o modo padrão é `fit`: mostra o produto inteiro, sem distorcer nem
 * cortar, e sem prejudicar imagens largas (que continuam preenchendo a largura).
 *
 * Formato: https://img.imageboss.me/{source}/{operacao}/{dimensoes}/{publicId}
 */
const IMAGEBOSS_BASE = `https://img.imageboss.me/${import.meta.env.VITE_IMAGEBOSS_SOURCE || 'sischef-images'}`;

export interface ImagemRef {
  url?: string | null;
  publicId?: string | null;
}

export interface OpcoesImagem {
  largura: number;
  altura: number;
  /**
   * `fit` (padrão) → imagem inteira, sem recorte (ImageBoss `width`). Use com
   * `object-fit: contain` na <img>.
   * `cover` → preenche LxA recortando o excesso (ImageBoss `cover:center`).
   */
  modo?: 'fit' | 'cover';
}

/**
 * Retorna a URL pública da imagem do produto. Usa ImageBoss quando há
 * `publicId`; cai na `url` gravada no anexo para registros legados sem key.
 */
export function urlImagemProduto(
  imagem: ImagemRef | null | undefined,
  opcoes: OpcoesImagem,
): string | undefined {
  if (!imagem) return undefined;

  if (imagem.publicId) {
    const { largura, altura, modo = 'fit' } = opcoes;

    if (modo === 'cover') {
      return `${IMAGEBOSS_BASE}/cover:center/${largura}x${altura}/${imagem.publicId}`;
    }

    // `fit`: limita pela maior dimensão da caixa preservando a proporção. A <img>
    // com `object-fit: contain` centraliza o resultado dentro da caixa final.
    // `withoutEnlargement` evita ampliar (e borrar) imagens menores que a caixa.
    const limite = Math.max(largura, altura);
    return `${IMAGEBOSS_BASE}/width/${limite}/withoutEnlargement:true/${imagem.publicId}`;
  }

  return imagem.url ?? undefined;
}
